import { useEffect, useMemo, useState } from 'react';
import type { MerchantProfile } from '@/types/api/merchant';
import { fetchLiveGooglePlacePhotosWithCache } from '@/utils/liveGooglePlacePhotos';
import {
  buildExplicitMerchantImages,
  mergeExplicitAndLiveGooglePhotos,
  type LivePlacePhoto,
  type StorefrontGalleryImage,
} from '@/utils/merchantStorefrontGallery';

/**
 * Gallery for buyer-facing merchant storefront: API media first, then live Google Places when `googlePlaceId` is set.
 */
export function useMerchantStorefrontGallery(
  merchant: MerchantProfile | null
): { galleryImages: StorefrontGalleryImage[] } {
  const placeId = useMemo(() => merchant?.googlePlaceId?.trim() ?? '', [merchant?.googlePlaceId]);

  const [live, setLive] = useState<LivePlacePhoto[] | null>(null);

  useEffect(() => {
    setLive(null);
    if (!placeId) return;

    let cancelled = false;
    void (async () => {
      const items = await fetchLiveGooglePlacePhotosWithCache(placeId);
      if (!cancelled) setLive(items);
    })();

    return () => {
      cancelled = true;
    };
  }, [placeId]);

  const galleryImages = useMemo((): StorefrontGalleryImage[] => {
    if (!merchant) return [];

    const fullApi = buildExplicitMerchantImages(merchant);

    if (!placeId) {
      return fullApi;
    }

    if (live === null) {
      return fullApi;
    }

    if (live.length === 0) {
      return fullApi;
    }

    const explicitOnly = buildExplicitMerchantImages(merchant, { forLiveGoogleAugmentation: true });
    return mergeExplicitAndLiveGooglePhotos(explicitOnly, live, placeId);
  }, [merchant, placeId, live]);

  return { galleryImages };
}
